import { useEffect, useState } from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { observer } from 'mobx-react-lite'

import { Form, getFiltersObject, useNoDigitalSlots, useNoDigitalCalendarStore } from '@shared/ui'
import {
  originalFormatDate,
  useRouteState,
  getLocalStorageItem,
  setLocalStorageItem,
} from '@shared/utils'
import { SRUserName } from '@sr/ui'
import { NotifyChangesModal } from '../../common/modal-agenda/components/care/notify-changes-modal'

import { useLayoutContext } from '../../common'
import {
  ConfirmationModal,
  ScheduleAgendaLayout,
  ScheduleNoDigitalCheckbox,
} from './components'

import { FORM_NAME } from './enums'
import { FormValues } from './interfaces'
import { ScheduleRouteState } from './state'

export const Schedule = observer(() => {
  const { id } = useParams<{ id: string }>()
  const { setIsLoading } = useLayoutContext()

  const {
    policy,
    isLoading,
    setScheduleFilters,
    schedules,
    savePolicyOnSchedule,
    loadDependencies,
    isMod,
  } = useRouteState(ScheduleRouteState, {
    policyId: id,
  })

  const methods = useForm<FormValues>()
  const { watch, setValue, handleSubmit, reset } = methods
  const noDigital = watch(FORM_NAME.noDigital)
  
  const { selectedSlotData, assignSlot } = useNoDigitalSlots({ policyId: id || '', enabled: noDigital && !!id })

  const firstAppointmentDate = policy?.firstAppointmentDate

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [modifyDate, setModifyDate] = useState<Date | string | null>(null)
  const [previousDate] = useState<Date | string | null>(null)
  const [isNotifyModalOpen, setIsNotifyModalOpen] = useState<boolean>(false)
  const [defaultNotifyClient, setDefaultNotifyClient] = useState<boolean>(true)
  const [pendingForm, setPendingForm] = useState<FormValues | null>(null)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const dateSelected = getLocalStorageItem('dateSelected') || modifyDate

  useEffect(() => {
    setIsLoading(isLoading || isSubmitting)
  }, [isLoading, isSubmitting])

  useEffect(() => {
    setValue(FORM_NAME.firstAppointmentDate, null)
    const isSr = true
    setScheduleFilters(getFiltersObject({ noDigital, isSr }))
  }, [noDigital, setScheduleFilters, setValue])

  useEffect(() => {
    const lastPolicyId = getLocalStorageItem('lastPolicyId')
    
    if (lastPolicyId && lastPolicyId !== id) {
      console.log('🧹 SR Schedule: Policy changed from', lastPolicyId, 'to', id, '- cleaning localStorage')
      setLocalStorageItem('dateSelected', null)
      setLocalStorageItem('slotsSelected', null)
      setLocalStorageItem('previousDateSelected', null)
      setLocalStorageItem('clicked', null)
    }
    
    if (id) {
      setLocalStorageItem('lastPolicyId', id)
    }
  }, [id])

  useEffect(() => {
    if (policy) {
      console.log('🔄 SR Schedule: Initializing form for policy', id, 'isMod:', isMod, 'noDigital:', policy.noDigital)
      
      // Only reset the form if it hasn't been initialized yet or if the policy actually changed
      const currentFormValue = watch(FORM_NAME.noDigital)
      const shouldReset = currentFormValue === undefined
      
      console.log('🔄 SR Schedule: Form reset decision:', {
        shouldReset,
        currentFormValue,
        policyNoDigital: policy.noDigital
      })
      
      if (shouldReset) {
        reset({
          noDigital: policy.noDigital,
        })
      }
      
      if (isMod) {
        setModifyDate(originalFormatDate(firstAppointmentDate))
        
        const storedDateSelected = getLocalStorageItem('dateSelected')
        if (storedDateSelected && !modifyDate) {
          console.log('📅 SR Schedule: Preserving stored date selection during modification:', storedDateSelected)
        }
      }
    }
  }, [policy, isMod, firstAppointmentDate, reset, id, watch])

  if (!policy) return null

  const executeSubmit = async (form: FormValues, shouldNotifyClient = true) => {
    setIsSubmitting(true)
    const { originQida } = form
    
    if (noDigital && selectedSlotData) {
      let success = true
      
      const shouldDeleteExisting = (isMod && id) || (id && policy?.firstAppointmentDate)
      
      if (shouldDeleteExisting) {
        const store = useNoDigitalCalendarStore.getState()
        const appointmentDeleted = await store.deleteAppointment(id)
        
        if (!appointmentDeleted) {
          console.error('❌ SR: Failed to delete existing appointment, continuing with modification...')
        }
      }
      
      success = await assignSlot()
      if (success) {
        if (policy) {
          policy.update({ noDigital: true });
        }
  
        const firstAppointmentDate = getLocalStorageItem('dateSelected')
        await savePolicyOnSchedule(
          { ...form, firstAppointmentDate, notifyClient: shouldNotifyClient },
          { avoidUpdate: true, originQida },
        )
        await loadDependencies()
        setIsModalOpen(true)
      }
    } else {
      const firstAppointmentDate = getLocalStorageItem('dateSelected')
      await savePolicyOnSchedule(
        { ...form, firstAppointmentDate, notifyClient: shouldNotifyClient },
        { avoidUpdate: true, originQida },
      )
      await loadDependencies()
      setIsModalOpen(true)
    }
    setIsSubmitting(false)
  }

  const onSubmit: SubmitHandler<FormValues> = async (form) => {
    if (isMod) {
      setPendingForm(form)
      setDefaultNotifyClient(true)
      setIsNotifyModalOpen(true)
      return
    }

    await executeSubmit(form, true)
  }

  const onClose = () => {
    setIsModalOpen(false)
  }

  const closeNotifyModal = () => {
    setIsNotifyModalOpen(false)
    setPendingForm(null)
  }

  const handleNotifyConfirm = async (shouldNotifyClient: boolean) => {
    setIsNotifyModalOpen(false)
    if (pendingForm) {
      await executeSubmit(pendingForm, shouldNotifyClient)
      setPendingForm(null)
    }
  }

  const key = isMod ? 'common:modifyAppointment' : 'common:createAppointment'

  return (
    <div className="py-4">
      <SRUserName fullName={policy.fullName} />
      <Form
        className="grid grid-cols-12 gap-8"
        methods={methods}
        dataTestId="schedule-social-referent"
      >
        <ScheduleNoDigitalCheckbox
          isMod={isMod}
          setModifyDate={setModifyDate}
          name={FORM_NAME.noDigital}
        />
        <ScheduleAgendaLayout
          buttonText={key}
          onSubmit={onSubmit}
          handleSubmit={handleSubmit}
          dateSelected={dateSelected || modifyDate}
          firstDateSelected={firstAppointmentDate}
          previousDate={previousDate}
          noDigital={noDigital}
          schedules={schedules}
          isMod={isMod}
          setModifyDate={(date) => {
            setModifyDate(date ? originalFormatDate(new Date(date)) : null)
          }}
          modifyDate={modifyDate ? new Date(modifyDate) : null}
          policyId={id}
        />
      </Form>
      {isModalOpen && <ConfirmationModal onClose={onClose} />}
      <NotifyChangesModal
        open={isNotifyModalOpen}
        onConfirm={handleNotifyConfirm}
        onClose={closeNotifyModal}
        isLoading={isSubmitting}
      />
    </div>
  )
})
